import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { useAtomValue } from "jotai";
import { ETaxonomy, IBenefitPackageView, IInitChatBotDocuments, ITransformedBenefitPackages } from "library/types";
import { extractFileNameFromUrl } from "library/utils";
import { useMemo } from "react";
import { useGetAttachments } from "state/queries/useGetAttachments";
import { useGetBenefitImplementations } from "state/queries/useGetBenefitImplementations";
import { useGetSingleClient } from "state/queries/useGetSingleClient";
import { useUserData } from "state/queries/useUserData";
import {
  companyPackagesState,
  onlyActiveImplementationsState,
  selectedCompanyState,
} from "store/UIHrPage";

export function useAllUserDocumentsForChatbot():
  | IInitChatBotDocuments[]
  | null {
  const { data: loggedUser } = useUserData();
  const hbhObjectType = useTaxonomyOptions(ETaxonomy.HbhObjectType);
  const companyPackageObject = useAtomValue(companyPackagesState);

  const benefitPackageType = hbhObjectType.find(
    (type) => type.code === "HbhObjectType.BenefitPackage"
  );

  const selectedCompanyWithNoAttachData = useAtomValue(selectedCompanyState);

  const loggedUserId = loggedUser?.mail || loggedUser?.userPrincipalName || "";

  const { data: selectedCompany } = useGetSingleClient({
    countryCode: selectedCompanyWithNoAttachData?.countryCode || 0,
    internalNumber:
      selectedCompanyWithNoAttachData?.clientInternalNumberGos || 0,
    enabled: !!selectedCompanyWithNoAttachData,
  });

  const onlyActiveImplementations = useAtomValue(
    onlyActiveImplementationsState
  );
  const { data: implementedBenefits } = useGetBenefitImplementations({
    enabled: !!selectedCompany,
    countryCode: Number(selectedCompany?.countryCode),
    clientInternalNumberGOS: Number(selectedCompany?.clientInternalNumberGos),
    benefitCategoryTag: 0,
    onlyActive: onlyActiveImplementations,
  });

  const { data: allCompanyPackagesAttachments } = useGetAttachments({
    countryCode: selectedCompany?.countryCode || 0,
    clientInternalNumberGOS: selectedCompany?.clientInternalNumberGos || 0,
    hbhObjectType: benefitPackageType?.value,
    enabled: !!selectedCompany,
  });

  const allDocuments = useMemo(() => {
    const documents = [] as IInitChatBotDocuments[];

    if (
      !implementedBenefits ||
      !selectedCompany ||
      !allCompanyPackagesAttachments
    )
      return null;
    implementedBenefits.forEach((benefit) => {
      const benefitDocuments = benefit?.attachments || [];
      benefitDocuments.forEach((document) => {
        const documentIndex = documents.findIndex(
          (doc) =>
            extractFileNameFromUrl(doc.DocumentPath) ===
            extractFileNameFromUrl(document.attachmentPath)
        );

        if (documentIndex !== -1) {
          // Document exists, check if user is already added
          const userExists = documents[documentIndex]?.Users.some(
            (user) => user.Id === loggedUserId
          );

          if (!userExists) {
            // Push new user if they are not already in the list
            documents[documentIndex]?.Users.push({
              Id: loggedUserId,
              Name: loggedUser?.displayName || "",
            });
          }
        } else {
          // Document doesn't exist, create new entry
          documents.push({
            ...(benefit?.name ? { ImplementationName: benefit?.name || "" } : {}),
            DocumentPath: document.attachmentPath || "",
            LastModifiedAt: document.lastModifiedAt || "",
            Users: [
              {
                Id: loggedUserId,
                Name: loggedUser?.displayName || "",
              },
            ],
          });
        }
      });
    });
    (selectedCompany?.attachments || []).forEach((attachment) => {
      const attachmentPath = attachment.attachmentPath || "";
      const fileName = extractFileNameFromUrl(attachmentPath);

      const documentExists = documents.some(
        (doc) => extractFileNameFromUrl(doc.DocumentPath) === fileName
      );

      if (!documentExists) {
        documents.push({
          DocumentPath: attachmentPath,
          LastModifiedAt: attachment.lastModifiedAt || "",
          Users: [
            {
              Id: loggedUserId,
              Name: loggedUser?.displayName || "",
            },
          ],
        });
      }
    });
    //TODO documents from packages

    (allCompanyPackagesAttachments || []).forEach((attachment) => {
      const attachmentPath = attachment.attachmentPath || "";
      const fileName = extractFileNameFromUrl(attachmentPath);
   
      const benefitPackage = companyPackageObject?.[attachment?.attachmentObjectTag || ""];
      const benefitPackageName = benefitPackage?.name || "";
      const benefitImplementationName = benefitPackage?.benefitImplementationName || "";
      const documentExists = documents.some(
        (doc) => extractFileNameFromUrl(doc.DocumentPath) === fileName
      );

      if (!documentExists) {
        documents.push({
          ...(benefitPackageName ? { PackageName: benefitPackageName } : {}),
          ...(benefitImplementationName ? { ImplementationName: benefitImplementationName } : {}),
          DocumentPath: attachmentPath,
          LastModifiedAt: attachment.lastModifiedAt || "",
          Users: [
            {
              Id: loggedUserId,
              Name: loggedUser?.displayName || "",
            },
          ],
        });
      }
    });
    return documents;
  }, [implementedBenefits, selectedCompany, allCompanyPackagesAttachments]);

  if (!selectedCompany || !implementedBenefits) return null;

  return allDocuments;
}
